/**
 * Track-screen demo seam — Phase 1 · Step 6.
 *
 * The Admin / Staff / Client tracks are the visible product. Their REAL
 * data comes from the Project Ledger + engagementTemplate + conversation
 * Firestore collections (Sprint 1–5 plumbing, reused verbatim). Real
 * multi-project listing/selection is the project-CRUD sprint; until then
 * this module supplies a small, deterministic demo project set so the
 * three tracks are fully walkable today WITHOUT forking the live VP656
 * conversation flow (it is included as the first row, unchanged).
 *
 * PURE + dependency-light (only the engagementTemplate config). No
 * Firebase, no React — the Node test runner exercises the derived views
 * directly. The screens read THIS for the list/summary scaffolding and
 * read the live Firestore libs for the one demo project's real thread.
 */
import {
  canEnterStage,
  getEngagementTemplate,
  resolveGateAmount,
  stageRailFor,
  type EngagementTemplate,
  type Tier,
} from './engagementTemplate';
import type { GateState } from './engagementTemplate';
import { emptyGateState } from './projectLedger';
import { demoSeedEnabled } from './demoSeed';

export interface DemoProject {
  pid: string;
  vp: string;
  /**
   * The project's own human-readable name (2026-05-22) — distinct from the
   * client name and the `VP###` code. Optional: demo-seed rows and any
   * pre-field project doc have none; render `projectLabel(...)` for a
   * graceful `VP### · <tier>` placeholder when this is empty.
   */
  projectName?: string;
  clientName: string;
  clientNo: string;
  tier: Tier;
  /** Stage key the project currently sits at. */
  activeStageKey: string;
  /** Assigned staff (commit-rights holder for this project). */
  assigneeUid: string;
  assigneeName: string;
  /** Total engagement value in INR (for the money overview). */
  valueINR: number;
  /** Money gate ids already cleared (drives "paid / due"). */
  clearedGateIds: string[];
  /** Open Review-Queue proposal count (admin RQ aggregate). */
  openProposals: number;
  /**
   * AGREED-PRICE FIX (2026-06-15) — billing currency. Optional (demo seeds /
   * pre-field docs default INR). Drives the money-display symbol so a USD
   * engagement never renders ₹.
   */
  currency?: 'INR' | 'USD';
  /**
   * AGREED-PRICE FIX (2026-06-15) — per-gate expected amounts set from the
   * agreed price (the $40 fix). When present, a gate id here WINS over the
   * template default. Optional → every existing demo seed stays valid.
   */
  gateAmounts?: Record<string, number>;
}

/**
 * The demo set. Row 1 (VP656) is the LIVE conversation fixture
 * (`DEMO_PID`) — its thread/proposals/ledger are real Firestore data; the
 * other rows are static scaffolding so the list/aggregate screens are
 * meaningful. assigneeUid 'u-khilendra' is the demo signed-in staff
 * (commit-rights demo).
 */
export const DEMO_PROJECTS: DemoProject[] = [
  {
    pid: 'VP656',
    vp: 'VP656',
    clientName: 'Sitaramji Agarwal',
    clientNo: 'AV-C-0560',
    tier: 'L2B',
    activeStageKey: 'r1_eval',
    assigneeUid: 'u-khilendra',
    assigneeName: 'Khilendra',
    valueINR: 51000,
    clearedGateIds: ['stage1'],
    openProposals: 3,
  },
  {
    pid: 'VP701',
    vp: 'VP701',
    clientName: 'Meera Krishnan',
    clientNo: 'AV-C-0612',
    tier: 'L1B',
    activeStageKey: 'audit',
    assigneeUid: 'u-khilendra',
    assigneeName: 'Khilendra',
    valueINR: 11000,
    clearedGateIds: ['advance'],
    openProposals: 1,
  },
  {
    pid: 'VP744',
    vp: 'VP744',
    clientName: 'Rakesh Goenka',
    clientNo: 'AV-C-0644',
    tier: 'L3B',
    activeStageKey: 'p2_concept',
    // Round 3 (FIX 3, #920): point demo assignees at REAL roster ops people
    // (responsibilityTags.ROSTER) so the ?who= preview is coherent —
    // ?who=u-kunjal now shows Kunjal's actual assigned project.
    assigneeUid: 'u-kunjal',
    assigneeName: 'Kunjal',
    valueINR: 880000,
    clearedGateIds: ['phase1', 'phase2'],
    openProposals: 5,
  },
  {
    pid: 'VP758',
    vp: 'VP758',
    clientName: 'Deshmukh Family',
    clientNo: 'AV-C-0659',
    tier: 'L2C',
    activeStageKey: 'p2_services',
    assigneeUid: 'u-sukant',
    assigneeName: 'Sukant',
    valueINR: 240000,
    clearedGateIds: ['part1'],
    openProposals: 0,
  },
];

/**
 * The SCREEN-FACING project-scaffold accessor (task #992). Returns the
 * demo rows ONLY when the demo seed is explicitly enabled; otherwise
 * EMPTY so every screen shows nothing but live Firestore projects. The
 * `DEMO_PROJECTS` const above is intentionally still exported — the Node
 * contract tests import it directly as a deterministic fixture; only the
 * UI goes through this gate. Pass an explicit list to bypass the gate
 * (used by tests / aggregate functions that receive their own input).
 */
export function seedProjects(): DemoProject[] {
  return demoSeedEnabled() ? DEMO_PROJECTS : [];
}

/** The demo signed-in staff member (drives the assignee/commit demo). */
export const DEMO_STAFF = { uid: 'u-khilendra', name: 'Khilendra' };

export function templateOf(p: DemoProject): EngagementTemplate {
  return getEngagementTemplate(p.tier) ?? getEngagementTemplate('L2B')!;
}

/** Money split for a project — what's paid vs what's still gated. */
export interface MoneyView {
  pid: string;
  clientName: string;
  valueINR: number;
  paidINR: number;
  dueINR: number;
  /** Per money-gate: id, label, amount (resolved), cleared? */
  gates: Array<{
    id: string;
    label: string;
    amountINR: number;
    cleared: boolean;
  }>;
}

/**
 * Resolve a project's money picture from its LOCKED engagementTemplate
 * gate config (amounts are NOT invented here — fixed gates use their
 * amountINR; %-split gates resolve against the project value). This is the
 * same money-before-delivery contract the Ledger sprint enforces; the
 * track screen only VISUALISES it.
 */
export function moneyViewOf(p: DemoProject): MoneyView {
  const tpl = templateOf(p);
  const gates = tpl.gates
    .filter((g): g is Extract<typeof g, { kind: 'money' }> => g.kind === 'money')
    .map((g) => {
      // THE ONE CANONICAL RESOLVER — agreed `gateAmounts` override wins over
      // the template default (the $40 fix). Same `resolveGateAmount` the
      // money module + Record-a-payment modal call (one source of truth).
      const amount = resolveGateAmount(g, p.valueINR, p.gateAmounts);
      return {
        id: g.id,
        label: g.label,
        amountINR: amount,
        cleared: p.clearedGateIds.includes(g.id),
      };
    });
  const paidINR = gates
    .filter((g) => g.cleared)
    .reduce((s, g) => s + g.amountINR, 0);
  const totalGated = gates.reduce((s, g) => s + g.amountINR, 0);
  return {
    pid: p.pid,
    clientName: p.clientName,
    valueINR: p.valueINR,
    paidINR,
    dueINR: Math.max(0, totalGated - paidINR),
    gates,
  };
}

/** Stage rail rows for a demo project (reuses the engagementTemplate). */
export function railOf(p: DemoProject) {
  return stageRailFor(templateOf(p), p.activeStageKey);
}

/** Admin cross-project aggregate (the admin track's reason to exist). */
export interface AdminAggregate {
  projectCount: number;
  totalValueINR: number;
  totalPaidINR: number;
  totalDueINR: number;
  openProposals: number;
  /** Per-assignee workload (open proposals + project count). */
  workload: Array<{
    assigneeUid: string;
    assigneeName: string;
    projects: number;
    openProposals: number;
  }>;
}

export function adminAggregate(
  projects: DemoProject[] = seedProjects(),
): AdminAggregate {
  const money = projects.map(moneyViewOf);
  const wl = new Map<
    string,
    { assigneeUid: string; assigneeName: string; projects: number; openProposals: number }
  >();
  for (const p of projects) {
    const w =
      wl.get(p.assigneeUid) ?? {
        assigneeUid: p.assigneeUid,
        assigneeName: p.assigneeName,
        projects: 0,
        openProposals: 0,
      };
    w.projects += 1;
    w.openProposals += p.openProposals;
    wl.set(p.assigneeUid, w);
  }
  return {
    projectCount: projects.length,
    totalValueINR: projects.reduce((s, p) => s + p.valueINR, 0),
    totalPaidINR: money.reduce((s, m) => s + m.paidINR, 0),
    totalDueINR: money.reduce((s, m) => s + m.dueINR, 0),
    openProposals: projects.reduce((s, p) => s + p.openProposals, 0),
    workload: [...wl.values()].sort((a, b) =>
      a.assigneeName.localeCompare(b.assigneeName),
    ),
  };
}

/* ════════════════════ Canonical project source (Round 2) ════════════════════
 *
 * OWNER (Round 2, 2026-05-18 · item 4): the Ancillary project picker must
 * use the REAL/canonical project source on this branch, not a demo-only
 * literal duplicated inside the screen. On this branch the canonical
 * project source IS this module's project set (every screen — Clients,
 * Money, Workload, Ancillary — reads it; Row 1 VP656 is live Firestore).
 * A live all-projects Firestore listing is the project-CRUD sprint.
 *
 * These two accessors are the SINGLE seam the picker + billing consult so
 * the source is defined in ONE place. When the CRUD sprint lands a real
 * `projects` collection listener, ONLY these two functions change — the
 * Ancillary screen does not. */

/** Real attachable-project list for the ancillary picker (canonical). */
export function attachableProjects(): Array<{
  pid: string;
  clientName: string;
  clientNo: string;
  tier: Tier;
}> {
  // #992: empty when the demo seed is off — the Ancillary picker then
  // shows only real attachable projects (the live source is wired
  // upstream; this seam returns nothing fabricated).
  return seedProjects().map((p) => ({
    pid: p.pid,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tier: p.tier,
  }));
}

/**
 * Real known-client directory (canonical) — drives the Round-2
 * "standalone-but-already-a-client → existing client number" rule. Derived
 * from the same canonical project source (one client row per distinct
 * clientNo). Phone/email are not on DemoProject yet (the CRUD sprint adds
 * the real client record); until then this carries name + clientNo so the
 * resolver is wired to the real source and the matching keys fill in when
 * the client CRUD record lands. */
export function knownClients(): Array<{
  clientNo: string;
  name: string;
  phone?: string;
  email?: string;
}> {
  const seen = new Set<string>();
  const out: Array<{ clientNo: string; name: string }> = [];
  for (const p of seedProjects()) {
    if (seen.has(p.clientNo)) continue;
    seen.add(p.clientNo);
    out.push({ clientNo: p.clientNo, name: p.clientName });
  }
  return out;
}

/** Projects assigned to a given staff uid (staff track home). */
export function projectsForStaff(
  uid: string,
  projects: DemoProject[] = seedProjects(),
): DemoProject[] {
  return projects.filter((p) => p.assigneeUid === uid);
}

/**
 * PURE client-side search predicate for the Clients/Projects list.
 *
 * Filters the ALREADY-LOADED list only — no extra Firestore reads (the
 * brief constraint). Matches a case-insensitive substring of the trimmed
 * query against ANY of the fields that are actually present on the loaded
 * row: client name, client number (AV-C-####), project code (VP###), the
 * displayed tier text, the assignee name, and the active-stage key.
 *
 * NOTE (honest scope): phone / project-type / location live on the
 * `clients/{clientNo}` master doc, which the LIST does not load (only the
 * per-row Edit panel fetches it on demand). Searching those would force a
 * fan-out of reads the constraint forbids, so they are intentionally NOT
 * matched here. Name + number + code + tier + assignee + stage cover the
 * fields the row already has. An empty/whitespace query matches all.
 */
export function matchesClientSearch(
  row: DemoProject,
  tierText: string,
  query: string,
): boolean {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.projectName,
    row.clientName,
    row.clientNo,
    row.vp,
    tierText,
    row.assigneeName,
    row.activeStageKey,
  ]
    .filter(Boolean)
    .join('  ')
    .toLowerCase();
  return hay.includes(q);
}

/** ₹ formatter — Indian grouping, no decimals. */
export function inr(n: number): string {
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/* ════════════════════ CLIENT money + journey timeline ════════════════════
 *
 * OWNER CORRECTION (memory project_avbp_conversation_visibility_model,
 * 2026-05-17): MONEY IS CLIENT-VISIBLE. The client must see, per project
 * AND across all their projects: amount paid, amount due, and exactly
 * WHAT TO PAY NEXT to unlock the report / next stage. Client-hidden =
 * ONLY the internal AI/review machinery (AI drafts, Review Queue,
 * internal notes, workload/assignee mechanics) — NOT money.
 *
 * The spine filter (visibleThread / isClientVisible) is UNCHANGED — that
 * governs the CONVERSATION thread (AI/review layer stays hidden there).
 * This is a SEPARATE client-facing money/journey surface; it deliberately
 * carries money (the previously mis-hidden thing) and nothing else
 * internal. Amounts are NOT invented — they resolve from the LOCKED
 * engagementTemplate gates via `moneyViewOf` (one pricing path).
 * ──────────────────────────────────────────────────────────────────── */

/** One dated event on the client's journey timeline. */
export interface ClientJourneyEvent {
  /** ISO date the event occurred / is projected. */
  date: string;
  kind: 'quote' | 'payment' | 'report' | 'stage';
  label: string;
  /** Signed INR effect on the running balance (payments reduce due). */
  amountINR: number;
  /** Running OUTSTANDING (due) after this event. Client-facing. */
  balanceAfterINR: number;
}

export interface ClientProjectMoney {
  pid: string;
  clientName: string;
  tier: Tier;
  displayName: string;
  valueINR: number;
  paidINR: number;
  dueINR: number;
  /**
   * The single NEXT payment the client must make to unlock the next
   * stage / the report — null when nothing is currently due. This is the
   * owner-locked "what to pay next to unlock" surface.
   */
  nextPayment: {
    gateId: string;
    label: string;
    amountINR: number;
    /** Plain-language: what clearing this unlocks. */
    unlocks: string;
  } | null;
  /** Dated timeline (quote → payments → report), running balance shown. */
  timeline: ClientJourneyEvent[];
}

/**
 * Build the client-facing money + journey timeline for ONE project.
 * Reuses `moneyViewOf` (locked gate amounts) — the figures are identical
 * to what Admin sees in /money; the client simply now SEES them too
 * (owner correction). The "next payment" is the first uncleared money
 * gate, and what it unlocks is read from the engagementTemplate stage it
 * blocks (not invented).
 */
export function clientProjectMoney(p: DemoProject): ClientProjectMoney {
  const tpl = templateOf(p);
  const m = moneyViewOf(p);
  // Deterministic demo dates anchored to a fixed clock (no Date.now —
  // same discipline as the rest of the demo layer).
  const base = Date.parse('2026-05-01T00:00:00.000Z');
  const dayISO = (d: number) =>
    new Date(base + d * 86400000).toISOString().slice(0, 10);

  const timeline: ClientJourneyEvent[] = [];
  let running = m.gates.reduce((s, g) => s + g.amountINR, 0); // full due at quote

  // Quote event — the full engagement value becomes "due".
  timeline.push({
    date: dayISO(0),
    kind: 'quote',
    label: `Quote prepared · ${tpl.displayName} — ${inr(m.valueINR)}`,
    amountINR: m.valueINR,
    balanceAfterINR: running,
  });

  // Payments — each cleared gate is a dated payment that reduces the
  // outstanding balance (in gate order).
  let dayCursor = 3;
  for (const g of m.gates) {
    if (g.cleared) {
      running = Math.max(0, running - g.amountINR);
      timeline.push({
        date: dayISO(dayCursor),
        kind: 'payment',
        label: `Payment received · ${g.label}`,
        amountINR: -g.amountINR,
        balanceAfterINR: running,
      });
      dayCursor += 4;
    }
  }

  // The next uncleared money gate = "what to pay next to unlock".
  const nextGate = m.gates.find((g) => !g.cleared) ?? null;
  let nextPayment: ClientProjectMoney['nextPayment'] = null;
  if (nextGate) {
    const tplGate = tpl.gates.find(
      (gg) => gg.kind === 'money' && gg.id === nextGate.id,
    );
    const blockKey =
      tplGate && 'blocksStageKey' in tplGate
        ? tplGate.blocksStageKey
        : null;
    const blockedStage = blockKey
      ? tpl.stages.find((s) => s.key === blockKey)
      : null;
    nextPayment = {
      gateId: nextGate.id,
      label: nextGate.label,
      amountINR: nextGate.amountINR,
      unlocks: blockedStage
        ? `Unlocks: ${blockedStage.label}`
        : 'Unlocks the next stage of your engagement',
    };
  }

  return {
    pid: p.pid,
    clientName: p.clientName,
    tier: p.tier,
    displayName: tpl.displayName,
    valueINR: m.valueINR,
    paidINR: m.paidINR,
    dueINR: m.dueINR,
    nextPayment,
    timeline,
  };
}

export interface ClientMoneySummary {
  projectCount: number;
  totalValueINR: number;
  totalPaidINR: number;
  totalDueINR: number;
  /** Across ALL the client's projects: the immediate next thing to pay. */
  nextAcrossProjects: {
    pid: string;
    displayName: string;
    amountINR: number;
    unlocks: string;
  } | null;
  perProject: ClientProjectMoney[];
}

/**
 * The cross-project client money summary (owner-locked: the client sees,
 * across ALL their projects, total paid / total due / the next payment
 * that unlocks the next step). `projects` defaults to the demo set; a
 * real client's projects are filtered upstream. NO AI/review/internal
 * data — money + journey only.
 */
export function clientMoneySummary(
  projects: DemoProject[] = seedProjects(),
): ClientMoneySummary {
  const per = projects.map(clientProjectMoney);
  const withNext = per.find((p) => p.nextPayment != null) ?? null;
  return {
    projectCount: per.length,
    totalValueINR: per.reduce((s, p) => s + p.valueINR, 0),
    totalPaidINR: per.reduce((s, p) => s + p.paidINR, 0),
    totalDueINR: per.reduce((s, p) => s + p.dueINR, 0),
    nextAcrossProjects:
      withNext && withNext.nextPayment
        ? {
            pid: withNext.pid,
            displayName: withNext.displayName,
            amountINR: withNext.nextPayment.amountINR,
            unlocks: withNext.nextPayment.unlocks,
          }
        : null,
    perProject: per,
  };
}

/**
 * Defence-in-depth: prove the client money surface is computed ONLY from
 * the locked gate config + cleared-gate state — never from the AI/review
 * layer. Returns true iff the client can ENTER no stage they haven't paid
 * for (money-before-delivery still holds for the client view). Used by a
 * test to assert the surface didn't accidentally leak an ungated stage.
 */
export function clientMoneyRespectsGates(p: DemoProject): boolean {
  const tpl = templateOf(p);
  const state: GateState = {
    ...emptyGateState(),
    moneyPaid: Object.fromEntries(p.clearedGateIds.map((id) => [id, true])),
  };
  // Any money gate NOT cleared must still block its stage for the client.
  return tpl.gates
    .filter((g) => g.kind === 'money' && !p.clearedGateIds.includes(g.id))
    .every(
      (g) =>
        'blocksStageKey' in g &&
        !canEnterStage(tpl, g.blocksStageKey, state).allowed,
    );
}
